import { convexQuery } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import {
  locationsGetAllMine,
  organizationRolesEnsureDefaultRolesExist,
  organizationRolesGetRoles,
} from "~/api-refs";
import {
  EmployeeLocationField,
  EmployeeRolesField,
} from "~/components/employees/employee-fields";
import { convexClient } from "~/integrations/convex/provider";

import { Button } from "../ui/button";
import { Form } from "../ui/form";
import type { Step3Data } from "./common";

interface EmployeeFormStep3Props {
  onOpenCreateRoleModal: (name: string) => void;
  onSaveData: (data: Step3Data) => void;
  onSubmit: (data: Step3Data) => void;
  data?: Step3Data;
}

export function EmployeeFormStep3({
  onOpenCreateRoleModal,
  onSubmit,
  onSaveData,
  data,
}: EmployeeFormStep3Props) {
  const { data: locations } = useQuery(convexQuery(locationsGetAllMine, {}));

  const { data: roles } = useQuery(convexQuery(organizationRolesGetRoles, {}));

  // Mutation to ensure default roles exist
  const ensureRolesMutation = useMutation({
    mutationFn: () =>
      convexClient.mutation(organizationRolesEnsureDefaultRolesExist, {}),
  });

  // Auto-create default roles if none exist
  useEffect(() => {
    if (roles !== undefined && roles.length === 0) {
      ensureRolesMutation.mutate();
    }
  }, [roles, ensureRolesMutation.mutate, ensureRolesMutation]);

  const form = useForm<Step3Data>({
    values: data ?? {
      locations: locations?.map((r) => r.id) ?? [],
      roles: [],
      addToFutureLocations: false,
    },
  });

  const handleSubmit = (formData: Step3Data) => {
    // Validate that at least one role is selected
    if (formData.roles.length === 0) {
      form.setError("roles", {
        type: "manual",
        message: "Please select at least one role for the employee",
      });
      return;
    }
    onSubmit(formData);
  };

  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(handleSubmit)}>
        <EmployeeLocationField
          control={form.control}
          locations={locations}
          name="locations"
          onAllLocationsSelectedChange={(allSelected) =>
            form.setValue("addToFutureLocations", allSelected)
          }
        />
        <div>
          <EmployeeRolesField
            control={form.control}
            name="roles"
            createNewOption={(name) => {
              onSaveData(form.getValues());
              onOpenCreateRoleModal(name);
            }}
          />
          {form.formState.errors.roles && (
            <p className="text-destructive mt-2 text-sm font-medium">
              {form.formState.errors.roles.message}
            </p>
          )}
        </div>
        <Button
          className="w-full"
          disabled={ensureRolesMutation.isPending}
          size="lg"
          type="submit"
        >
          {ensureRolesMutation.isPending
            ? "Setting up roles..."
            : "Create Employee"}
        </Button>
      </form>
    </Form>
  );
}
